import { toast } from "react-toastify"

import { REQ } from "constants/api/requests"
import { IResponsePayload } from "constants/interfaces"

import useApi from "util/api"

import {
  ActionLogsResponse,
  AutoActionRule,
  BlacklistEntry,
  BlacklistFilters,
  BlacklistListResponse,
  BlacklistType,
  DuplicateMatch,
  DuplicateMatchesFilters,
  DuplicateSettings,
  FlaggedPlayersFilters,
  FlaggedPlayersResponse,
  GlobalDuplicateMatchesResponse,
  IRiskService,
  PlayerRiskHistoryEntry,
  PlayerRiskProfile,
  ReviewQueueFilters,
  ReviewQueueResponse,
  RiskDashboardData,
  RiskLevelSettings
} from "./types"

class RiskService implements IRiskService {
  public async getDashboard(): Promise<RiskDashboardData | void> {
    try {
      const { data } = await useApi.get<IResponsePayload<RiskDashboardData>>(REQ.ADMIN_DASHBOARD.RISK.DASHBOARD)
      if (data?.success) return data.response.payload
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to load risk dashboard")
    }
  }

  public async getFlaggedPlayers(filters: FlaggedPlayersFilters): Promise<FlaggedPlayersResponse | void> {
    try {
      const { data } = await useApi.get<IResponsePayload<FlaggedPlayersResponse>>(REQ.ADMIN_DASHBOARD.RISK.PLAYERS, { params: filters })
      if (data?.success) return data.response.payload
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to load flagged players")
    }
  }

  public async getPlayerRisk(userId: string): Promise<PlayerRiskProfile | void> {
    try {
      const { data } = await useApi.get<IResponsePayload<PlayerRiskProfile>>(REQ.ADMIN_DASHBOARD.RISK.PLAYER.replace(":userId", userId))
      if (data?.success) return data.response.payload
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to load player risk")
    }
  }

  public async getPlayerHistory(userId: string): Promise<PlayerRiskHistoryEntry[] | void> {
    try {
      const { data } = await useApi.get<IResponsePayload<PlayerRiskHistoryEntry[]>>(
        REQ.ADMIN_DASHBOARD.RISK.PLAYER_HISTORY.replace(":userId", userId)
      )
      if (data?.success) return data.response.payload
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to load risk history")
    }
  }

  public async getPlayerDuplicates(userId: string): Promise<DuplicateMatch[] | void> {
    try {
      const { data } = await useApi.get<IResponsePayload<DuplicateMatch[]>>(
        REQ.ADMIN_DASHBOARD.RISK.PLAYER_DUPLICATES.replace(":userId", userId)
      )
      if (data?.success) return data.response.payload
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to load duplicates")
    }
  }

  public async reviewDuplicate(userId: string, matchId: string, decision: string): Promise<void> {
    try {
      await useApi.patch(REQ.ADMIN_DASHBOARD.RISK.REVIEW_DUPLICATE.replace(":userId", userId).replace(":matchId", matchId), { decision })
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to review duplicate")
    }
  }

  public async triggerManualAction(userId: string, action: string, reason: string): Promise<void> {
    try {
      await useApi.post(REQ.ADMIN_DASHBOARD.RISK.MANUAL_ACTION.replace(":userId", userId), { action, reason })
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to trigger action")
    }
  }

  // SOFT-567: do NOT swallow the error here. The risk score is fail-closed — in microservice mode a
  // downed FS/NUX/bonus-service authority returns 503 instead of scoring on a stale shadow. The
  // caller must distinguish that from success (no false "recalculated" toast) and show a retryable
  // message, so let the error propagate to the component.
  public async recalculatePlayer(userId: string): Promise<void> {
    await useApi.post(REQ.ADMIN_DASHBOARD.RISK.RECALCULATE_PLAYER.replace(":userId", userId))
  }

  public async recalculateAll(): Promise<{ total: number; processed: number; errors: number } | void> {
    try {
      const { data } = await useApi.post<IResponsePayload<{ total: number; processed: number; errors: number }>>(
        REQ.ADMIN_DASHBOARD.RISK.RECALCULATE_ALL
      )
      if (data?.success) return data.response.payload
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to recalculate all")
    }
  }

  public async getRiskLevelSettings(): Promise<RiskLevelSettings | void> {
    try {
      const { data } = await useApi.get<IResponsePayload<RiskLevelSettings>>(REQ.ADMIN_DASHBOARD.RISK.SETTINGS_LEVELS)
      if (data?.success) return data.response.payload
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to load settings")
    }
  }

  public async updateRiskLevelSettings(settings: RiskLevelSettings): Promise<void> {
    try {
      await useApi.put(REQ.ADMIN_DASHBOARD.RISK.SETTINGS_LEVELS, settings)
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to update settings")
    }
  }

  public async getDuplicateSettings(): Promise<DuplicateSettings | void> {
    try {
      const { data } = await useApi.get<IResponsePayload<DuplicateSettings>>(REQ.ADMIN_DASHBOARD.RISK.SETTINGS_DUPLICATES)
      if (data?.success) return data.response.payload
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to load duplicate settings")
    }
  }

  public async updateDuplicateSettings(settings: Partial<DuplicateSettings>): Promise<void> {
    try {
      await useApi.put(REQ.ADMIN_DASHBOARD.RISK.SETTINGS_DUPLICATES, settings)
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to update duplicate settings")
    }
  }

  public async listAutoActions(): Promise<AutoActionRule[] | void> {
    try {
      const { data } = await useApi.get<IResponsePayload<AutoActionRule[]>>(REQ.ADMIN_DASHBOARD.RISK.ACTIONS)
      if (data?.success) return data.response.payload
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to load auto-actions")
    }
  }

  public async createAutoAction(rule: Omit<AutoActionRule, "id" | "execution_count" | "last_executed_at" | "created_at">): Promise<void> {
    try {
      await useApi.post(REQ.ADMIN_DASHBOARD.RISK.ACTIONS, rule)
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to create auto-action")
    }
  }

  public async updateAutoAction(id: string, rule: Partial<AutoActionRule>): Promise<void> {
    try {
      await useApi.put(REQ.ADMIN_DASHBOARD.RISK.ACTION_DETAIL.replace(":id", id), rule)
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to update auto-action")
    }
  }

  public async deleteAutoAction(id: string): Promise<void> {
    try {
      await useApi.delete(REQ.ADMIN_DASHBOARD.RISK.ACTION_DETAIL.replace(":id", id))
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to delete auto-action")
    }
  }

  public async toggleAutoAction(id: string): Promise<void> {
    try {
      await useApi.patch(REQ.ADMIN_DASHBOARD.RISK.ACTION_TOGGLE.replace(":id", id))
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to toggle auto-action")
    }
  }

  public async getActionLogs(filters: { page: number; pageSize: number }): Promise<ActionLogsResponse | void> {
    try {
      const { data } = await useApi.get<IResponsePayload<ActionLogsResponse>>(REQ.ADMIN_DASHBOARD.RISK.ACTION_LOGS, { params: filters })
      if (data?.success) return data.response.payload
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to load action logs")
    }
  }

  public async getBlacklistEntries(filters: BlacklistFilters): Promise<BlacklistListResponse | void> {
    try {
      const { data } = await useApi.get<IResponsePayload<BlacklistListResponse>>(REQ.ADMIN_DASHBOARD.RISK.BLACKLIST, { params: filters })
      if (data?.success) return data.response.payload
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to load blacklist")
    }
  }

  public async createBlacklistEntry(entry: { type: BlacklistType; value: string; reason?: string }): Promise<BlacklistEntry | void> {
    try {
      const { data } = await useApi.post<IResponsePayload<BlacklistEntry>>(REQ.ADMIN_DASHBOARD.RISK.BLACKLIST, entry)
      if (data?.success) return data.response.payload
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to create blacklist entry")
    }
  }

  public async updateBlacklistEntry(id: string, entry: Partial<BlacklistEntry>): Promise<BlacklistEntry | void> {
    try {
      const { data } = await useApi.patch<IResponsePayload<BlacklistEntry>>(
        REQ.ADMIN_DASHBOARD.RISK.BLACKLIST_ENTRY.replace(":id", id),
        entry
      )
      if (data?.success) return data.response.payload
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to update blacklist entry")
    }
  }

  public async deleteBlacklistEntry(id: string): Promise<void> {
    try {
      await useApi.delete(REQ.ADMIN_DASHBOARD.RISK.BLACKLIST_ENTRY.replace(":id", id))
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to delete blacklist entry")
    }
  }

  // ─── Review Queue ──────────────────────────────────────────────────────────

  public async getReviewQueue(filters: ReviewQueueFilters): Promise<ReviewQueueResponse | void> {
    try {
      const { data } = await useApi.get<IResponsePayload<ReviewQueueResponse>>(REQ.ADMIN_DASHBOARD.RISK.REVIEW_QUEUE, { params: filters })
      if (data?.success) return data.response.payload
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to load review queue")
    }
  }

  public async getPendingReviewCount(): Promise<number> {
    try {
      const res = await this.getReviewQueue({ page: 1, pageSize: 1, status: "PENDING" })
      return res?.total ?? 0
    } catch {
      return 0
    }
  }

  public async resolveReviewItem(id: string, decision: string, resolution?: string): Promise<void> {
    try {
      await useApi.patch(REQ.ADMIN_DASHBOARD.RISK.REVIEW_QUEUE_RESOLVE.replace(":id", id), { decision, resolution })
    } catch (err: any) {
      console.log("err: ", err)
      toast.error(err?.data?.message || "Failed to resolve review item")
    }
  }

  public async getDuplicateMatches(filters: DuplicateMatchesFilters): Promise<GlobalDuplicateMatchesResponse | void> {
    try {
      const { data } = await useApi.get<IResponsePayload<GlobalDuplicateMatchesResponse>>(REQ.ADMIN_DASHBOARD.RISK.DUPLICATES, {
        params: filters
      })
      if (data?.success) return data.response.payload
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to load duplicate matches")
    }
  }

  public async reviewDuplicateMatch(matchId: string, decision: string): Promise<void> {
    try {
      await useApi.patch(REQ.ADMIN_DASHBOARD.RISK.DUPLICATES_REVIEW.replace(":matchId", matchId), { decision })
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to review duplicate match")
    }
  }
}

export const riskService = new RiskService()
